/**
 * Utilitário para captura e gerenciamento de UTMs e dados de tracking
 * 
 * Captura UTMs da URL, armazena no localStorage e envia para o backend
 * quando o usuário se cadastra para permitir atribuição correta de conversões.
 */

import api from '../config/axios';

// Chaves de armazenamento
const UTM_STORAGE_KEY = 'gio_utm_data';
const SESSION_ID_KEY = 'gio_session_id';
const TRACKING_IDS_KEY = 'gio_tracking_ids';

/**
 * Gera ou recupera um ID de sessão único
 */
const getOrCreateSessionId = () => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = crypto.randomUUID ? crypto.randomUUID() : 
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : ((r & 0x3) | 0x8);
        return v.toString(16);
      });
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

/**
 * Captura cookies de tracking (ga, fbp, etc.)
 */
const captureTrackingCookies = () => {
  const cookies = {};
  
  try {
    const cookieString = document.cookie;
    const cookieList = cookieString.split(';').map(c => c.trim());
    
    // Cookies importantes para tracking
    const trackingCookieNames = [
      '_ga',           // Google Analytics
      '_gid',          // Google Analytics (sessão)
      '_gac_',         // Google Ads conversion
      '_gcl_au',       // Google Conversion Linker
      '_gcl_aw',       // Google Ads click
      '_gclid',        // Google Click ID
      '_fbp',          // Facebook Pixel
      '_fbc',          // Facebook Click ID
      '_ttp',          // TikTok Pixel (first-party)
      'ttp',           // TikTok (alguns ambientes)
      '_ttclid',       // TikTok Click ID persistido (GTM usa este nome)
      'ttclid',        // TikTok Click ID (nome alternativo)
      '_uetmsclkid',   // Bing Ads Click ID
      '_pin_unauth',   // Pinterest
      '_derived_epik', // Pinterest
      'dub_id',        // Dub Partners (clickId)
    ];
    
    cookieList.forEach(cookie => {
      const [name, value] = cookie.split('=');
      if (trackingCookieNames.some(tc => name.startsWith(tc))) {
        cookies[name] = value;
      }
    });
  } catch (e) {
    console.warn('Erro ao capturar cookies de tracking:', e);
  }
  
  return cookies;
};

/**
 * Monta _fbc no formato Meta: fb.1.{creationTimeMs}.{fbclid}
 */
const buildFbcFromFbclid = (fbclid) => {
  if (!fbclid || typeof fbclid !== 'string') return null;
  const cleaned = fbclid.trim();
  if (!cleaned) return null;
  return `fb.1.${Date.now()}.${cleaned}`;
};

/**
 * Se a URL tiver fbclid e o cookie _fbc ainda não existir, cria _fbc no formato Meta.
 * Garante matching CAPI mesmo quando o Pixel não setou o cookie a tempo.
 */
const ensureFbcCookieFromFbclid = (fbclid) => {
  if (!fbclid || typeof document === 'undefined') return null;

  try {
    const existing = document.cookie.match(/(?:^|;\s*)_fbc=([^;]*)/)?.[1];
    if (existing) {
      return decodeURIComponent(existing);
    }

    const fbc = buildFbcFromFbclid(fbclid);
    if (!fbc) return null;

    const maxAge = 90 * 24 * 60 * 60; // 90 dias (padrão Meta)
    const hostname = window.location.hostname.replace(/^www\./i, '');
    const domainPart = hostname.includes('.') ? `; domain=.${hostname}` : '';
    document.cookie = `_fbc=${encodeURIComponent(fbc)}; Max-Age=${maxAge}; path=/; SameSite=Lax${domainPart}`;
    console.log('📊 UTM Capture: Cookie _fbc criado a partir do fbclid', fbc);
    return fbc;
  } catch (e) {
    console.warn('Erro ao criar cookie _fbc a partir do fbclid:', e);
    return null;
  }
};

/**
 * Captura informações do dispositivo/navegador
 */
const captureDeviceInfo = () => {
  try {
    const ua = navigator.userAgent;
    
    // Detectar tipo de dispositivo
    const isMobile = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    const isTablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(ua);
    
    // Detectar SO
    let os = 'Unknown';
    let osVersion = '';
    if (/Windows NT/i.test(ua)) {
      os = 'Windows';
      const match = ua.match(/Windows NT ([\d.]+)/);
      osVersion = match ? match[1] : '';
    } else if (/Mac OS X/i.test(ua)) {
      os = 'macOS';
      const match = ua.match(/Mac OS X ([\d._]+)/);
      osVersion = match ? match[1].replace(/_/g, '.') : '';
    } else if (/Android/i.test(ua)) {
      os = 'Android';
      const match = ua.match(/Android ([\d.]+)/);
      osVersion = match ? match[1] : '';
    } else if (/iOS|iPhone|iPad|iPod/i.test(ua)) {
      os = 'iOS';
      const match = ua.match(/OS ([\d_]+)/);
      osVersion = match ? match[1].replace(/_/g, '.') : '';
    } else if (/Linux/i.test(ua)) {
      os = 'Linux';
    }
    
    // Detectar navegador
    let browser = 'Unknown';
    let browserVersion = '';
    if (/Chrome/i.test(ua) && !/Edge|Edg/i.test(ua)) {
      browser = 'Chrome';
      const match = ua.match(/Chrome\/([\d.]+)/);
      browserVersion = match ? match[1] : '';
    } else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) {
      browser = 'Safari';
      const match = ua.match(/Version\/([\d.]+)/);
      browserVersion = match ? match[1] : '';
    } else if (/Firefox/i.test(ua)) {
      browser = 'Firefox';
      const match = ua.match(/Firefox\/([\d.]+)/);
      browserVersion = match ? match[1] : '';
    } else if (/Edge|Edg/i.test(ua)) {
      browser = 'Edge';
      const match = ua.match(/Edg(?:e)?\/([\d.]+)/);
      browserVersion = match ? match[1] : '';
    }
    
    return {
      type: isTablet ? 'tablet' : (isMobile ? 'mobile' : 'desktop'),
      os,
      os_version: osVersion,
      browser,
      browser_version: browserVersion,
      user_agent: ua,
      language: navigator.language || navigator.userLanguage || 'pt-BR',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
      screen: {
        width: window.screen?.width || 0,
        height: window.screen?.height || 0,
        resolution: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
      },
      viewport: {
        width: window.innerWidth || 0,
        height: window.innerHeight || 0,
      },
    };
  } catch (e) {
    console.warn('Erro ao capturar info do dispositivo:', e);
    return {};
  }
};

/**
 * Captura UTMs da URL atual e salva no localStorage
 * Deve ser chamado em TODAS as páginas (useEffect no App.js ou router)
 */
export const captureUTMsOnPageLoad = () => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = getOrCreateSessionId();
    
    // Verificar se há UTMs ou IDs de clique na URL
    const utmParams = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'utm_id',
      'utm_source_platform',
      'utm_trial',
    ];
    const clickIds = ['gclid', 'fbclid', 'ttclid', 'msclkid', 'li_fat_id'];
    
    const hasUTMs = utmParams.some(param => urlParams.has(param));
    const hasClickIds = clickIds.some(param => urlParams.has(param));
    
    if (!hasUTMs && !hasClickIds) {
      // Sem dados de tracking na URL, verificar se já temos dados salvos
      const existing = localStorage.getItem(UTM_STORAGE_KEY);
      if (!existing) {
        // Salvar pelo menos a landing page e referrer (visita direta)
        const directVisitData = {
          session_id: sessionId,
          landing_page: window.location.href.split('?')[0],
          referrer: document.referrer || null,
          timestamp: new Date().toISOString(),
          is_direct: true,
        };
        localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(directVisitData));
        console.log('📊 UTM Capture: Visita direta registrada');
      }
      return;
    }
    
    // Capturar dados de UTM
    const utmData = {
      session_id: sessionId,
      utm_source: urlParams.get('utm_source') || null,
      utm_medium: urlParams.get('utm_medium') || null,
      utm_campaign: urlParams.get('utm_campaign') || null,
      utm_content: urlParams.get('utm_content') || null,
      utm_term: urlParams.get('utm_term') || null,
      utm_id: urlParams.get('utm_id') || null,
      utm_source_platform: urlParams.get('utm_source_platform') || null,
      utm_trial: urlParams.get('utm_trial') || null,
      gclid: urlParams.get('gclid') || null,
      fbclid: urlParams.get('fbclid') || null,
      ttclid: urlParams.get('ttclid') || null,
      msclkid: urlParams.get('msclkid') || null,
      li_fat_id: urlParams.get('li_fat_id') || null,
      landing_page: window.location.href,
      referrer: document.referrer || null,
      timestamp: new Date().toISOString(),
      is_direct: false,
    };
    
    // Lógica de first-touch: só sobrescreve se não existir ou se tiver UTMs novos
    const existing = localStorage.getItem(UTM_STORAGE_KEY);
    const existingData = existing ? JSON.parse(existing) : null;
    
    // Se não existe first-touch ou se este tem UTMs mais completos, salvar
    if (!existingData || (hasUTMs && !existingData.utm_source)) {
      localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmData));
      console.log('📊 UTM Capture: UTMs capturados e salvos (first-touch)', utmData);
    } else {
      console.log('📊 UTM Capture: First-touch já existe, mantendo dados originais');
    }
    
    // Se veio fbclid e ainda não há _fbc, sintetizar cookie no formato Meta
    if (utmData.fbclid) {
      ensureFbcCookieFromFbclid(utmData.fbclid);
    }

    // Sempre salvar tracking IDs dos cookies separadamente
    const trackingCookies = captureTrackingCookies();
    if (Object.keys(trackingCookies).length > 0) {
      localStorage.setItem(TRACKING_IDS_KEY, JSON.stringify(trackingCookies));
    }
    
  } catch (e) {
    console.error('Erro ao capturar UTMs:', e);
  }
};

/**
 * Retorna os dados de UTM salvos no localStorage
 */
export const getSavedUTMData = () => {
  try {
    const data = localStorage.getItem(UTM_STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.error('Erro ao recuperar UTMs salvos:', e);
    return null;
  }
};

/**
 * Retorna os IDs de tracking salvos (cookies)
 */
export const getSavedTrackingIds = () => {
  try {
    const data = localStorage.getItem(TRACKING_IDS_KEY);
    return data ? JSON.parse(data) : {};
  } catch (e) {
    console.error('Erro ao recuperar tracking IDs:', e);
    return {};
  }
};

/**
 * Monta o objeto completo de tracking para enviar ao backend
 */
export const buildTrackingData = (userData = {}) => {
  const utmData = getSavedUTMData() || {};
  const trackingIds = getSavedTrackingIds();
  const deviceInfo = captureDeviceInfo();
  const sessionId = getOrCreateSessionId();
  
  // Montar estrutura completa
  const trackingData = {
    attribution: {
      first_touch: {
        utm_source: utmData.utm_source || null,
        utm_medium: utmData.utm_medium || null,
        utm_campaign: utmData.utm_campaign || null,
        utm_content: utmData.utm_content || null,
        utm_term: utmData.utm_term || null,
        utm_id: utmData.utm_id || null,
        utm_source_platform: utmData.utm_source_platform || null,
        utm_trial: utmData.utm_trial || null,
        gclid: utmData.gclid || null,
        fbclid: utmData.fbclid || null,
        ttclid: utmData.ttclid || null,
        msclkid: utmData.msclkid || null,
        li_fat_id: utmData.li_fat_id || null,
        landing_page: utmData.landing_page || window.location.href,
        referrer: utmData.referrer || document.referrer || null,
        timestamp: utmData.timestamp || new Date().toISOString(),
      },
      last_touch: {
        utm_source: utmData.utm_source || null,
        utm_medium: utmData.utm_medium || null,
        utm_campaign: utmData.utm_campaign || null,
        utm_content: utmData.utm_content || null,
        utm_term: utmData.utm_term || null,
        utm_id: utmData.utm_id || null,
        utm_source_platform: utmData.utm_source_platform || null,
        utm_trial: utmData.utm_trial || null,
        gclid: utmData.gclid || null,
        fbclid: utmData.fbclid || null,
        ttclid: utmData.ttclid || null,
        msclkid: utmData.msclkid || null,
        li_fat_id: utmData.li_fat_id || null,
        landing_page: utmData.landing_page || window.location.href,
        referrer: utmData.referrer || document.referrer || null,
        timestamp: new Date().toISOString(),
      },
      sessions_history: [
        {
          session_id: sessionId,
          utm_source: utmData.utm_source || null,
          utm_medium: utmData.utm_medium || null,
          utm_campaign: utmData.utm_campaign || null,
          timestamp: utmData.timestamp || new Date().toISOString(),
        }
      ],
    },
    user_data: {
      email_address: userData.email || null,
      phone_number: userData.telefone || userData.phone || null,
      address: {
        first_name: userData.nomeUsuario?.split(' ')[0] || userData.first_name || null,
        last_name: userData.nomeUsuario?.split(' ').slice(1).join(' ') || userData.last_name || null,
        city: userData.city || null,
        region: userData.region || userData.state || null,
        postal_code: userData.postal_code || userData.cep || null,
        country: userData.country || 'BR',
      },
    },
    session: {
      session_id: sessionId,
      device: {
        type: deviceInfo.type || 'desktop',
        os: deviceInfo.os || null,
        os_version: deviceInfo.os_version || null,
        browser: deviceInfo.browser || null,
        browser_version: deviceInfo.browser_version || null,
      },
      user_agent: deviceInfo.user_agent || navigator.userAgent,
      language: deviceInfo.language || 'pt-BR',
      timezone: deviceInfo.timezone || 'America/Sao_Paulo',
      screen: deviceInfo.screen || {},
      viewport: deviceInfo.viewport || {},
    },
    tracking_ids: {
      _ga: trackingIds._ga || null,
      _gid: trackingIds._gid || null,
      _gcl_au: trackingIds._gcl_au || null,
      _gclid: trackingIds._gclid || utmData.gclid || null,
      _fbp: trackingIds._fbp || null,
      _fbc: trackingIds._fbc || (utmData.fbclid ? buildFbcFromFbclid(utmData.fbclid) : null),
      ttp: trackingIds._ttp || trackingIds.ttp || null,
      ttclid: trackingIds._ttclid || trackingIds.ttclid || utmData.ttclid || null,
      _uetmsclkid: trackingIds._uetmsclkid || utmData.msclkid || null,
      dub_id: trackingIds.dub_id || null,
    },
    engagement: {
      first_visit_at: utmData.timestamp || new Date().toISOString(),
      last_visit_at: new Date().toISOString(),
      total_sessions: 1,
    },
    conversions: {},
    origin: {
      source: utmData.utm_source || (utmData.referrer ? 'referral' : 'direct'),
      medium: utmData.utm_medium || null,
      campaign: utmData.utm_campaign || null,
      is_direct: utmData.is_direct || (!utmData.utm_source && !utmData.referrer),
    },
  };
  
  return trackingData;
};

function readMauticContactId() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)mtc_id=([^;]*)/);
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Monta payload completo de tracking para captura de lead (/lead).
 * Inclui UTMs, click IDs, cookies de ads e metadados da conversão.
 */
export const buildLeadTrackingPayload = (formData, origin = "direct") => {
  const tracking = buildTrackingData({
    email: formData.email,
    telefone: formData.telefone,
    nomeUsuario: formData.nome,
    origin,
  });

  tracking.tracking_ids = {
    ...tracking.tracking_ids,
    mtc_id: readMauticContactId(),
  };

  tracking.conversions = {
    ...tracking.conversions,
    lead_captured: {
      timestamp: new Date().toISOString(),
      origin,
      source: "lead_landing",
      page: "/lead",
    },
  };

  return tracking;
};

/**
 * Salva os dados de tracking no backend quando o usuário se cadastra
 */
export const saveUserTracking = async (userId, userData = {}) => {
  if (!userId) {
    console.error('❌ saveUserTracking: userId é obrigatório');
    return null;
  }
  
  try {
    const trackingData = buildTrackingData(userData);
    
    // Adicionar timestamp de complete_registration
    trackingData.conversions.complete_registration = {
      timestamp: new Date().toISOString(),
      method: userData.registration_method || 'email',
      origin: userData.origin || trackingData.origin.source || 'direct',
    };
    
    console.log('📊 Salvando tracking do usuário:', {
      user_id: userId,
      first_touch_source: trackingData.attribution.first_touch.utm_source,
      first_touch_campaign: trackingData.attribution.first_touch.utm_campaign,
    });
    
    const response = await api.post('/api/user-utm-tracking/', {
      user_id: userId,
      tracking_data: trackingData,
    });
    
    console.log('✅ Tracking salvo com sucesso:', response.data);
    return response.data;
    
  } catch (error) {
    console.error('❌ Erro ao salvar tracking:', error);
    // Não propagar erro para não interromper o fluxo de cadastro
    return null;
  }
};

/**
 * Atualiza o tracking do usuário com uma nova conversão
 */
export const updateUserConversion = async (userId, conversionType, conversionData = {}) => {
  if (!userId) {
    console.error('❌ updateUserConversion: userId é obrigatório');
    return null;
  }
  
  try {
    const response = await api.patch('/api/user-utm-tracking/update/', {
      user_id: userId,
      conversion_type: conversionType,
      conversion_data: {
        timestamp: new Date().toISOString(),
        ...conversionData,
      },
    });
    
    console.log(`✅ Conversão '${conversionType}' registrada:`, response.data);
    return response.data;
    
  } catch (error) {
    console.error(`❌ Erro ao registrar conversão '${conversionType}':`, error);
    return null;
  }
};

/**
 * Busca os dados de tracking do usuário do backend
 */
export const getUserTracking = async (userId) => {
  if (!userId) {
    console.error('❌ getUserTracking: userId é obrigatório');
    return null;
  }
  
  try {
    const response = await api.get(`/api/user-utm-tracking/${userId}/`);
    return response.data;
  } catch (error) {
    console.error('❌ Erro ao buscar tracking:', error);
    return null;
  }
};

/**
 * Limpa os dados de UTM do localStorage (após salvar no backend)
 */
export const clearLocalUTMData = () => {
  try {
    localStorage.removeItem(UTM_STORAGE_KEY);
    localStorage.removeItem(TRACKING_IDS_KEY);
    console.log('🗑️ Dados de UTM locais limpos');
  } catch (e) {
    console.error('Erro ao limpar dados de UTM:', e);
  }
};

const utmCapture = {
  captureUTMsOnPageLoad,
  getSavedUTMData,
  getSavedTrackingIds,
  buildTrackingData,
  buildLeadTrackingPayload,
  saveUserTracking,
  updateUserConversion,
  getUserTracking,
  clearLocalUTMData,
};

export default utmCapture;
